import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  LexicalTypeaheadMenuPlugin,
  MenuOption,
  useBasicTypeaheadTriggerMatch,
} from '@lexical/react/LexicalTypeaheadMenuPlugin';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import {
  $getSelection,
  $isRangeSelection,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  createCommand,
} from 'lexical';
import clsx from 'clsx';

import { blockGroups, filterBlockOptions } from './blockOptions';

// The typeahead has no imperative close; it closes when its trigger text is
// gone. The + menu dispatches this so the two menus are never open together.
export const CLOSE_SLASH_MENU_COMMAND = createCommand('CLOSE_SLASH_MENU_COMMAND');

class SlashMenuOption extends MenuOption {
  constructor(option) {
    super(option.key);
    this.option = option;
  }
}

function GroupHeader({ children }) {
  return (
    <div className="px-3 py-1 text-xs text-gray-500 dark:text-muted-foreground not-first:mt-1">
      {children}
    </div>
  );
}

function MenuRow({ option, secondary, setRefElement, active, onClick, onMouseEnter }) {
  const Icon = option.icon;
  return (
    <button
      ref={setRefElement}
      type="button"
      onMouseDown={e => e.preventDefault()}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      className={clsx(
        'w-full flex items-center gap-2 px-3 py-1.5 text-left cursor-pointer outline-none',
        active
          ? 'bg-gray-100 dark:bg-muted text-black dark:text-foreground'
          : 'text-gray-700 dark:text-muted-foreground hover:bg-gray-50 dark:hover:bg-muted',
      )}
    >
      <Icon className="w-4 h-4 flex-shrink-0" />
      <span className="truncate">{option.label}</span>
      {secondary && (
        <span className="truncate text-gray-400 dark:text-muted-foreground">· {secondary}</span>
      )}
    </button>
  );
}

function renderOptions(
  options,
  query,
  lineHasContent,
  selectedIndex,
  selectOption,
  setHighlighted,
) {
  const row = (o, i, secondary) => (
    <MenuRow
      key={o.option.key}
      option={o.option}
      secondary={secondary}
      setRefElement={el => o.setRefElement(el)}
      active={i === selectedIndex}
      onClick={() => {
        setHighlighted(i);
        selectOption(o);
      }}
      onMouseEnter={() => setHighlighted(i)}
    />
  );

  const convertLabel = o => (o.option.group === 'text' ? 'Turn into' : null);

  // Filtering drops the groups, so each convert row says what it does instead.
  if (query) {
    return (
      <>
        <GroupHeader>Filtered results</GroupHeader>
        {options.map((o, i) => row(o, i, convertLabel(o)))}
      </>
    );
  }

  return (
    <>
      {blockGroups.map(group => {
        const members = options
          .map((o, i) => ({ o, i }))
          .filter(({ o }) => o.option.group === group.key);
        if (members.length === 0) return null;
        return (
          <React.Fragment key={group.key}>
            <GroupHeader>{group.heading}</GroupHeader>
            {members.map(({ o, i }) => row(o, i, lineHasContent ? convertLabel(o) : null))}
          </React.Fragment>
        );
      })}
    </>
  );
}

// Inside the typeahead's update a dispatched command runs at once while a nested
// update waits, so the query must be removed in the same update as the option.
export function selectSlashOption(editor, option, textNodeContainingQuery) {
  editor.update(() => {
    if (textNodeContainingQuery) {
      textNodeContainingQuery.remove();
    }
    option.onSelect(editor);
  });
}

export default function SlashCommandPlugin() {
  const [editor] = useLexicalComposerContext();
  const [query, setQuery] = useState('');
  const [lineHasContent, setLineHasContent] = useState(false);
  const queryRef = useRef(null);

  useEffect(
    () =>
      editor.registerCommand(
        CLOSE_SLASH_MENU_COMMAND,
        () => {
          const activeQuery = queryRef.current;
          if (activeQuery === null) return false;
          editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection) || selection.anchor.type !== 'text') return;
            const node = selection.anchor.getNode();
            const end = selection.anchor.offset;
            const length = activeQuery.length + 1;
            if (
              end >= length &&
              node.getTextContent().slice(end - length, end) === `/${activeQuery}`
            ) {
              node.spliceText(end - length, length, '');
            }
          });
          return true;
        },
        COMMAND_PRIORITY_LOW,
      ),
    [editor],
  );

  // Convert options act on the current line. On an empty line that reads as an
  // insert; on a line with text it is a real conversion, so the rows say so.
  const onQueryChange = useCallback(
    nextQuery => {
      queryRef.current = nextQuery;
      setQuery(nextQuery);
      editor.getEditorState().read(() => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) return;
        const { anchor } = selection;
        const anchorNode = anchor.getNode();
        const block = anchorNode.getTopLevelElement();
        if (!block) {
          setLineHasContent(false);
          return;
        }
        let text = block.getTextContent();
        // Drop the "/query" at the caret, wherever in the line the caret is.
        if (nextQuery !== null && anchor.type === 'text') {
          let end = anchor.offset;
          for (const node of block.getAllTextNodes()) {
            if (node.is(anchorNode)) break;
            end += node.getTextContentSize();
          }
          const start = end - nextQuery.length - 1;
          if (start >= 0) text = text.slice(0, start) + text.slice(end);
        }
        setLineHasContent(text.trim().length > 0);
      });
    },
    [editor],
  );

  const triggerFn = useBasicTypeaheadTriggerMatch('/', { minLength: 0 });

  const options = useMemo(
    () => filterBlockOptions(query).map(o => new SlashMenuOption(o)),
    [query],
  );

  const onSelect = useCallback(
    (selected, textNodeContainingQuery, closeMenu) => {
      selectSlashOption(editor, selected.option, textNodeContainingQuery);
      closeMenu();
    },
    [editor],
  );

  return (
    <LexicalTypeaheadMenuPlugin
      onQueryChange={onQueryChange}
      onSelectOption={onSelect}
      triggerFn={triggerFn}
      options={options}
      commandPriority={COMMAND_PRIORITY_CRITICAL}
      menuRenderFn={(
        anchorElementRef,
        { selectedIndex, selectOptionAndCleanUp, setHighlightedIndex },
      ) => {
        if (!anchorElementRef.current || options.length === 0) return null;
        return createPortal(
          <div className="w-60 max-h-80 overflow-y-auto bg-white dark:bg-popover border border-gray-100 dark:border-border rounded-md shadow-lg py-1 text-sm mt-[3px]">
            {renderOptions(
              options,
              query,
              lineHasContent,
              selectedIndex,
              selectOptionAndCleanUp,
              setHighlightedIndex,
            )}
          </div>,
          anchorElementRef.current,
        );
      }}
    />
  );
}
